/**
 * A one-sheet .xlsx writer (ADR 264) — enough OOXML to hand a customer a real
 * spreadsheet, and no more.
 *
 * Written by hand rather than pulled from a library: the alternative is a
 * §2.4 dependency for what is a zip and six small XML parts, and the backend
 * already reads OOXML the same way (`XlsxGrid`, stdlib zip + StAX, no POI).
 *
 * What the format buys over the CSV it replaces is types. A date written as a
 * serial with a date format sorts and filters as a date; a quantity written as a
 * number right-aligns and sums. `dd/MM/yyyy` text in a CSV does neither, which is
 * why the CSV had to hard-code a pt-BR date order in the first place — the cell
 * format now carries that, and the viewer's Excel locale renders it.
 *
 * Entries are STORED, not deflated. `CompressionStream` would shrink the largest
 * realistic sheet (JSL, ~16k rows) from about 5 MB to well under one, but it makes
 * the whole call async for a file that is downloaded once, by hand. Excel, Sheets
 * and LibreOffice all open a stored archive without comment.
 */

export type XlsxValue = string | number | Date | null;

export interface XlsxColumn {
  /** Header text. Doubles as the table column name, so it must be unique. */
  header: string;
  /** Character width, Excel's own unit. */
  width: number;
  type: "text" | "number" | "date";
}

export interface XlsxSheet {
  /** Worksheet tab name. Excel forbids : \ / ? * [ ] and 31+ characters. */
  name: string;
  columns: XlsxColumn[];
  /** One array per row, aligned to `columns`. */
  rows: XlsxValue[][];
}

const XML_DECL = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const NS_MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const NS_REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const NS_PKG_REL = "http://schemas.openxmlformats.org/package/2006/relationships";

/** Style indices, in the order `cellXfs` declares them below. Index 0 is the
 *  default and is never written: omitting `s` is the same thing, in less XML. */
const S_HEADER = 1;
const S_DATE = 2;

/** Excel's serial epoch. 1899-12-30, not the 31st: the 1900 leap-year bug it
 *  preserves for Lotus compatibility shifts everything by one day. */
const EPOCH_UTC = Date.UTC(1899, 11, 30);
const DAY_MS = 86_400_000;

export function excelSerial(d: Date): number {
  // Read the date in UTC. Callers pass a date whose UTC fields are the calendar
  // date they mean; a local-zone read would land on the previous day west of
  // Greenwich, which is precisely where this product runs.
  return Math.round((Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - EPOCH_UTC) / DAY_MS);
}

/** XML text escape. Control characters are dropped rather than escaped: they are
 *  illegal in XML 1.0 at any encoding, and one in a WMS description would make
 *  the whole file unopenable. */
function xml(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** A1-style column name: 1 → A, 27 → AA. */
export function colName(n: number): string {
  let out = "";
  for (let i = n; i > 0; i = Math.floor((i - 1) / 26)) {
    out = String.fromCharCode(65 + ((i - 1) % 26)) + out;
  }
  return out;
}

/** Excel rejects a defined name with spaces or punctuation, and silently repairs
 *  the file when it finds one — so the table's internal name is derived, never
 *  the sheet's display name. */
function definedName(s: string): string {
  const cleaned = s.normalize("NFD").replace(/[\u0300-\u036F]/g, "").replace(/[^A-Za-z0-9_]/g, "_");
  return /^[A-Za-z_]/.test(cleaned) ? cleaned : `T_${cleaned}`;
}

function cell(ref: string, value: XlsxValue, type: XlsxColumn["type"]): string {
  if (value === null || value === undefined || value === "") return "";
  if (type === "number") {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return "";
    return `<c r="${ref}"><v>${n}</v></c>`;
  }
  if (type === "date") {
    const d = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(d.getTime())) return "";
    return `<c r="${ref}" s="${S_DATE}"><v>${excelSerial(d)}</v></c>`;
  }
  // Inline strings, so the package needs no sharedStrings part. The saving a
  // shared table would give is real on repeated descriptions, and not worth a
  // seventh part plus a second pass over the rows.
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xml(String(value))}</t></is></c>`;
}

function worksheet(sheet: XlsxSheet): string {
  const lastCol = colName(sheet.columns.length);
  const lastRow = sheet.rows.length + 1;
  const dim = `A1:${lastCol}${lastRow}`;

  const cols = sheet.columns
    .map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${c.width}" customWidth="1"/>`)
    .join("");

  const header = sheet.columns
    .map((c, i) => `<c r="${colName(i + 1)}1" s="${S_HEADER}" t="inlineStr">`
      + `<is><t xml:space="preserve">${xml(c.header)}</t></is></c>`)
    .join("");

  const body = sheet.rows.map((row, r) => {
    const cells = sheet.columns
      .map((c, i) => cell(`${colName(i + 1)}${r + 2}`, row[i] ?? null, c.type))
      .join("");
    return `<row r="${r + 2}">${cells}</row>`;
  }).join("");

  return `${XML_DECL}
<worksheet xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">
<dimension ref="${dim}"/>
<sheetViews><sheetView workbookViewId="0">`
    // Freeze the header: 15k rows of addresses are unreadable once the column
    // names have scrolled away.
    + `<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>`
    + `<selection pane="bottomLeft" activeCell="A2" sqref="A2"/>`
    + `</sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
<cols>${cols}</cols>
<sheetData><row r="1" s="${S_HEADER}" customFormat="1">${header}</row>${body}</sheetData>
<tableParts count="1"><tablePart r:id="rId1"/></tableParts>
</worksheet>`;
}

/**
 * The ListObject: what makes the sheet a *table* rather than a range that happens
 * to have filter arrows. It carries the autofilter, the banded style, and header
 * names Excel then offers in formulas.
 *
 * The autofilter is declared here and NOT also on the worksheet. Both is a
 * duplicate definition over one range, and Excel answers that with the
 * "we found a problem with some content" repair prompt — the one outcome worse
 * than the CSV this replaces.
 */
function tablePart(sheet: XlsxSheet): string {
  const ref = `A1:${colName(sheet.columns.length)}${Math.max(sheet.rows.length + 1, 2)}`;
  const cols = sheet.columns
    .map((c, i) => `<tableColumn id="${i + 1}" name="${xml(c.header)}"/>`)
    .join("");
  const name = definedName(sheet.name);
  return `${XML_DECL}
<table xmlns="${NS_MAIN}" id="1" name="${name}" displayName="${name}" ref="${ref}" totalsRowShown="0">
<autoFilter ref="${ref}"/>
<tableColumns count="${sheet.columns.length}">${cols}</tableColumns>
<tableStyleInfo name="TableStyleMedium2" showFirstColumn="0" showLastColumn="0" showRowStripes="1" showColumnStripes="0"/>
</table>`;
}

/** Excel requires at least the two built-in fills, in this order, and a border
 *  entry — a styles part missing either is repaired on open. */
const STYLES = `${XML_DECL}
<styleSheet xmlns="${NS_MAIN}">
<numFmts count="1"><numFmt numFmtId="164" formatCode="dd/mm/yyyy"/></numFmts>
<fonts count="2">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="3">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

/** Sheet tab names: Excel's own forbidden set, and a hard 31-character limit. */
function tabName(s: string): string {
  const cleaned = s.replace(/[:\\/?*[\]]/g, " ").trim() || "Planilha1";
  return cleaned.slice(0, 31);
}

export function buildXlsx(input: XlsxSheet): Uint8Array {
  const sheet = { ...input, name: tabName(input.name) };
  const parts: Array<[string, string]> = [
    ["[Content_Types].xml", `${XML_DECL}
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
<Override PartName="/xl/tables/table1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.table+xml"/>
</Types>`],
    ["_rels/.rels", `${XML_DECL}
<Relationships xmlns="${NS_PKG_REL}">
<Relationship Id="rId1" Type="${NS_REL}/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`],
    ["xl/workbook.xml", `${XML_DECL}
<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">
<sheets><sheet name="${xml(sheet.name)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`],
    ["xl/_rels/workbook.xml.rels", `${XML_DECL}
<Relationships xmlns="${NS_PKG_REL}">
<Relationship Id="rId1" Type="${NS_REL}/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="${NS_REL}/styles" Target="styles.xml"/>
</Relationships>`],
    ["xl/styles.xml", STYLES],
    ["xl/worksheets/sheet1.xml", worksheet(sheet)],
    ["xl/worksheets/_rels/sheet1.xml.rels", `${XML_DECL}
<Relationships xmlns="${NS_PKG_REL}">
<Relationship Id="rId1" Type="${NS_REL}/table" Target="../tables/table1.xml"/>
</Relationships>`],
    ["xl/tables/table1.xml", tablePart(sheet)],
  ];
  return zipStore(parts);
}

// --------------------------------------------------------------------- zip

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

/**
 * A stored (method 0) zip. No compression, so no dependency and no async — see
 * the file header.
 *
 * The DOS timestamp is fixed rather than `Date.now()`: two exports of the same
 * run then produce byte-identical files, which is what lets a test assert on the
 * bytes and a customer diff two downloads.
 */
function zipStore(parts: Array<[string, string]>): Uint8Array {
  const enc = new TextEncoder();
  const DOS_TIME = 0;      // 00:00:00
  const DOS_DATE = 0x21;   // 1980-01-01

  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const [name, content] of parts) {
    const nameBytes = enc.encode(name);
    const data = enc.encode(content);
    const crc = crc32(data);

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);          // version needed
    lv.setUint16(6, 0x0800, true);      // UTF-8 filename flag
    lv.setUint16(8, 0, true);           // method 0 = stored
    lv.setUint16(10, DOS_TIME, true);
    lv.setUint16(12, DOS_DATE, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, data.length, true);
    lv.setUint32(22, data.length, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);

    const dir = new Uint8Array(46 + nameBytes.length);
    const dv = new DataView(dir.buffer);
    dv.setUint32(0, 0x02014b50, true);
    dv.setUint16(4, 20, true);          // version made by
    dv.setUint16(6, 20, true);          // version needed
    dv.setUint16(8, 0x0800, true);
    dv.setUint16(10, 0, true);
    dv.setUint16(12, DOS_TIME, true);
    dv.setUint16(14, DOS_DATE, true);
    dv.setUint32(16, crc, true);
    dv.setUint32(20, data.length, true);
    dv.setUint32(24, data.length, true);
    dv.setUint16(28, nameBytes.length, true);
    dv.setUint32(42, offset, true);
    dir.set(nameBytes, 46);

    chunks.push(local, data);
    central.push(dir);
    offset += local.length + data.length;
  }

  const centralSize = central.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, parts.length, true);
  ev.setUint16(10, parts.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const total = offset + centralSize + end.length;
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of [...chunks, ...central, end]) { out.set(c, at); at += c.length; }
  return out;
}
